import {
  ArrowLeftOutlined,
  InboxOutlined,
  InfoCircleOutlined
} from "@ant-design/icons";
import { useSubscription } from "@apollo/client";
import {
  Alert,
  Button,
  message,
  Modal,
  Select,
  Spin,
  Tooltip,
  Upload,
  UploadFile,
  UploadProps
} from "antd";
import { RcFile } from "antd/lib/upload";
import Dragger from "antd/lib/upload/Dragger";
import axios from "axios";
import ComputeStrategyTag from "components/manage-feature-wf-templates/widgets/compute-strategy-tag";
import { ComputeStrategy } from "components/workflow-template-editor/model";
import { COMPUTE_STRATEGY_LABELS, SidepanelStyles } from "constants/index";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import { FeatureTypes } from "entity-app/constants";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import { useContext, useEffect, useState } from "react";
import { BASE_URL } from "services/endpoints";
import { PROJECT_WORKFLOW_TEMPLATES_DD } from "services/graphQL/queries";
import { SUBSCRIPTION_IMPORT_FILE_PROCESSING } from "services/graphQL/subscriptions";
import { downloadStringAsCSVFile, downloadFileInBrowser } from "utils/utils";

/**
 * The "Standard Format" template offered in the Download Format section. It is
 * generated client-side from a fixed header list (one CSV header row) and
 * downloaded in the browser — no backend call.
 */
export type StandardTemplateConfig = {
  headers: string[];
  fileName: string;
};

export type ImportFeatureLogsProps = Readonly<{
  open: boolean;
  onCancel: () => void;
  onComplete?: () => void;
  featureType: FeatureTypes;
  entityLabelSingular: string;
  entityLabelPlural: string;
  standardTemplate: StandardTemplateConfig;
  /** When false, the workflow-template group is hidden and its query skipped. */
  showWorkflowTemplates?: boolean;
  failedFileName?: string;
}>;

/**
 * Generic feature-log CSV import modal (upload + async processing + summary),
 * generalized from the original material-only importer. Parameterized by
 * feature type, entity labels, standard-template headers, and whether
 * workflow-template formats are offered.
 */
export default function ImportFeatureLogs({
  open,
  onCancel,
  onComplete,
  featureType,
  entityLabelSingular,
  entityLabelPlural,
  standardTemplate,
  showWorkflowTemplates = false,
  failedFileName
}: ImportFeatureLogsProps) {
  const {
    tokenRetrievalState,
    gqlClientForProject,
    projectDetails
  }: TProjectContext = useContext(ProjectContext);

  // Enum value -> key name, e.g. FeatureTypes.BID_PACKAGE -> "BID_PACKAGE".
  const feature = FeatureTypes[featureType];

  const { data: workflowTemplatesData } = useCIQQuery(
    PROJECT_WORKFLOW_TEMPLATES_DD,
    {
      client: gqlClientForProject,
      variables: {
        featureId: featureType,
        projectId: projectDetails?.id
      },
      skip: !showWorkflowTemplates || !projectDetails?.id
    }
  );

  const workflowTemplates: Array<{
    id: string;
    name: string;
    dateblock_compute_strategy?: { key: ComputeStrategy; name: string };
  }> = showWorkflowTemplates
    ? (workflowTemplatesData?.project_workflow_templates ?? [])
    : [];

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [downloadFormatExpanded, setDownloadFormatExpanded] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string | undefined>(
    undefined
  );

  const [fileUploadId, setFileUploadId] = useState("");
  const [unsupportedFileSelected, setUnsupportedFileSelected] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(
    null
  );

  const [uploadingState, setUploadingState] = useState<{
    uploading: boolean;
    processingStatus: string;
    processedResults: any;
  }>({
    uploading: false,
    processingStatus: "",
    processedResults: null
  });

  const { data: fileProcessingData } = useSubscription(
    SUBSCRIPTION_IMPORT_FILE_PROCESSING,
    {
      shouldResubscribe: true,
      client: gqlClientForProject,
      skip: !fileUploadId,
      variables: {
        where: { id: { _eq: fileUploadId } }
      }
    }
  );

  const props: UploadProps = {
    accept: "text/csv",
    style: {
      borderColor: unsupportedFileSelected === true ? "var(--ciq-danger)" : ""
    },
    multiple: false,
    fileList,
    onChange: () => {},
    onRemove: () => {
      setFileList([]);
    },
    onDrop: (e) => {
      if (e.dataTransfer.files.length === 0) {
        return false;
      }

      const file = e.dataTransfer.files[0];
      if (file.type !== "text/csv") {
        setUnsupportedFileSelected(true);
        setFileList([]);
        message.error("Please select csv file only");
        return false;
      }

      setUnsupportedFileSelected(false);
      return true;
    },
    beforeUpload: (file: RcFile) => {
      if (file.type !== "text/csv") {
        setUnsupportedFileSelected(true);
        setFileList([]);
        message.error("Please select csv file only");
        return Upload.LIST_IGNORE;
      }
      setUnsupportedFileSelected(false);
      setUploadErrorMessage(null);
      setFileList([file]);
      return false;
    },
    showUploadList: {
      showDownloadIcon: false,
      showPreviewIcon: false
    }
  };

  const handleUpload = async () => {
    setUploadErrorMessage(null);
    setUploadingState({
      uploading: true,
      processingStatus: "",
      processedResults: null
    });
    const formData = new FormData();
    formData.append("file", fileList[0] as RcFile);
    formData.append("async", "true");

    let uploadResponse: any = null;
    const url = `${BASE_URL}/features/${feature}/import`;
    try {
      uploadResponse = await axios.post(url, formData, {
        headers: {
          authorization: `Bearer ${tokenRetrievalState.token}`
        }
      });
    } catch (ex: any) {
      const networkErrMsg = ex.message;
      const serverErrMsg = ex.response?.data?.error;

      setUploadingState({
        uploading: false,
        processingStatus: "",
        processedResults: null
      });

      if (ex.response?.status === 400) {
        setUploadErrorMessage(serverErrMsg || networkErrMsg);
        return;
      }

      message.error(`Import failed: ${serverErrMsg || networkErrMsg}`);
      onCancel();

      return;
    }

    if (uploadResponse.data?.success) {
      setUploadingState({
        uploading: false,
        processingStatus: "PROCESSING",
        processedResults: null
      });
      setFileUploadId(uploadResponse.data?.success);

      setFileList([]);
      onComplete?.();
    }
  };

  const handleBackToUpload = () => {
    setUploadingState({
      uploading: false,
      processingStatus: "",
      processedResults: null
    });
    setFileUploadId("");
    setFileList([]);
  };

  const downloadTemplateBlob = async (url: string, fileName: string) => {
    setDownloadingTemplate(true);
    try {
      const response = await axios.get(url, {
        headers: {
          authorization: `Bearer ${tokenRetrievalState.token}`
        },
        responseType: "blob"
      });

      const href = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.style.display = "none";
      link.href = href;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    } catch (ex: any) {
      const networkErrMsg = ex.message;
      const serverErrMsg = ex.response?.data?.error;
      message.error(
        `Template download failed: ${serverErrMsg || networkErrMsg}`
      );
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleDownloadTemplate = async () => {
    if (!selectedTemplate) return;

    if (selectedTemplate === "standard") {
      downloadStringAsCSVFile(
        [...standardTemplate.headers].join(","),
        standardTemplate.fileName
      );
      return;
    }

    const templateName =
      workflowTemplates.find(
        (t: { id: string; name: string }) => t.id === selectedTemplate
      )?.name ?? `${entityLabelSingular}_Template`;

    await downloadTemplateBlob(
      `${BASE_URL}/features/${feature}/${selectedTemplate}/import-template`,
      `${templateName}.csv`
    );
  };

  useEffect(() => {
    if (!fileProcessingData?.import_log.length) return;
    try {
      const statusComment = JSON.parse(
        fileProcessingData.import_log[0].status_comment
      );
      const processingStatus = fileProcessingData.import_log[0].status;
      setUploadingState({
        uploading: false,
        processingStatus,
        processedResults: statusComment
      });

      if (processingStatus === "PROCESSED" || processingStatus === "FAILED") {
        if (statusComment.File) {
          downloadFileInBrowser(
            statusComment.File,
            failedFileName ?? `Failed_${entityLabelSingular}_Log.csv`
          );
        }
      }
    } catch {
      message.error("An error occured while importing the log");
    }
    // Intentionally react only to new subscription data; the entity-label
    // props are stable for the lifetime of the modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileProcessingData]);

  const isImportInProgress =
    uploadingState.uploading ||
    uploadingState.processingStatus === "PROCESSING";

  const isImportComplete =
    uploadingState.processingStatus === "PROCESSED" ||
    uploadingState.processingStatus === "FAILED";

  return (
    <Modal
      className="custom-drawer"
      title={`Import ${entityLabelSingular} Log`}
      width={420}
      style={SidepanelStyles.style}
      styles={{ body: { height: "calc(100vh - 58px)" } }}
      footer={null}
      open={open}
      closable={!isImportInProgress}
      mask={{ closable: !isImportInProgress }}
      keyboard={!isImportInProgress}
      onCancel={() => {
        if (!isImportInProgress) {
          onCancel();
        }
      }}
      destroyOnHidden
      data-testid="index-modal-import-feature-log"
    >
      {" "}
      <div className="space-y-4 w-full">
        {isImportComplete ? (
          <div data-testid="index-import-summary">
            <button
              type="button"
              className="flex items-center gap-2 mb-4 text-ciq-brand bg-transparent border-none p-0 cursor-pointer"
              onClick={handleBackToUpload}
              data-testid="index-button-back-to-upload"
            >
              <ArrowLeftOutlined />
              <span className="font-semibold">Back to upload</span>
            </button>
            <p className="font-semibold mb-1 text-base">Import Summary</p>
            <p className="mb-3" style={{ opacity: "60%" }}>
              {entityLabelSingular} Import complete. Review the summary below.
            </p>
            <div
              className="w-full grid grid-cols-[1fr_auto] gap-x-4 gap-y-2 p-4 rounded whitespace-nowrap"
              style={{ backgroundColor: "var(--ciq-neutral-25)" }}
            >
              <div>Total</div>
              <div className="font-semibold text-right">
                {uploadingState.processedResults?.Total ?? 0}
              </div>
              <div>Added</div>
              <div className="font-semibold text-right">
                {uploadingState.processedResults?.Imported ?? 0}
              </div>
              <div>Updated</div>
              <div className="font-semibold text-right">
                {uploadingState.processedResults?.Updated ?? 0}
              </div>
              <div>Added/Updated With Errors</div>
              <div className="font-semibold text-right">
                {uploadingState.processedResults?.AddedUpdatedWithErrors ?? 0}
              </div>
              <div>Unchanged</div>
              <div className="font-semibold text-right">
                {uploadingState.processedResults?.Unchanged ?? 0}
              </div>
              <div>Failed</div>
              <div className="font-semibold text-right">
                {uploadingState.processedResults?.Failed ?? 0}
              </div>
            </div>
            <p
              className="mt-4 flex items-start gap-2"
              data-testid="index-import-summary-failure-note"
            >
              <InfoCircleOutlined
                className="mt-1 shrink-0"
                style={{ opacity: "60%" }}
              />
              <span>
                In case of Failure/Error a failure report will automatically be
                downloaded.
              </span>
            </p>
          </div>
        ) : (
          <>
            <div>
              <button
                type="button"
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  width: "100%",
                  background: "none",
                  border: "none",
                  padding: 0,
                  textAlign: "left"
                }}
                onClick={() => setDownloadFormatExpanded((prev) => !prev)}
                aria-expanded={downloadFormatExpanded}
                aria-label="Toggle download format section"
                data-testid="index-download-format-toggle"
              >
                <div style={{ flex: 1 }}>
                  <p
                    className="mb-1 font-semibold"
                    data-testid="index-download-format-title"
                  >
                    Download Format
                  </p>
                  <p
                    style={{
                      opacity: "60%",
                      fontSize: "12px",
                      margin: "0 0 16px 0"
                    }}
                    data-testid="index-download-format-desc"
                  >
                    Download pre-formatted .csv files to import a{" "}
                    {entityLabelSingular} Log.
                  </p>
                </div>
                <i
                  className="fas fa-chevron-up"
                  style={{
                    marginLeft: "8px",
                    marginBottom: "20px",
                    transform: downloadFormatExpanded
                      ? "rotate(0deg)"
                      : "rotate(180deg)",
                    transition: "transform 0.3s"
                  }}
                  data-testid="index-download-format-chevron"
                />
              </button>

              {downloadFormatExpanded && (
                <>
                  <div
                    className="flex items-center gap-2 mb-2"
                    data-testid="index-download-format-content"
                  >
                    <Select
                      placeholder="Select import format"
                      value={selectedTemplate}
                      onChange={(value) => setSelectedTemplate(value)}
                      style={{ flex: 1, minWidth: 0 }}
                      popupMatchSelectWidth={false}
                      styles={{ popup: { root: { minWidth: 360 } } }}
                      optionLabelProp="label"
                      data-testid="index-template-select"
                    >
                      <Select.OptGroup
                        label={
                          <span>
                            Import with Basic Details{" "}
                            <Tooltip
                              title={`Use this format to create ${entityLabelPlural} with the default ${entityLabelSingular} Template.`}
                            >
                              <InfoCircleOutlined style={{ opacity: "45%" }} />
                            </Tooltip>
                          </span>
                        }
                      >
                        <Select.Option
                          value="standard"
                          key="standard"
                          label="Standard Format"
                        >
                          Standard Format
                        </Select.Option>
                      </Select.OptGroup>
                      {showWorkflowTemplates &&
                        workflowTemplates.length > 0 && (
                          <Select.OptGroup
                            label={
                              <span>
                                Import with Durations and Links{" "}
                                <Tooltip
                                  title={`Use these formats to create ${entityLabelPlural} with the below ${entityLabelSingular} Templates. You can also add Durations and Links.`}
                                >
                                  <InfoCircleOutlined
                                    style={{ opacity: "45%" }}
                                  />
                                </Tooltip>
                              </span>
                            }
                          >
                            {workflowTemplates.map(
                              (template: {
                                id: string;
                                name: string;
                                dateblock_compute_strategy?: {
                                  key: ComputeStrategy;
                                  name: string;
                                };
                              }) => (
                                <Select.Option
                                  value={template.id}
                                  key={template.id}
                                  label={
                                    <span className="flex items-center gap-2 w-full min-w-0">
                                      <span className="truncate min-w-0 flex-1">
                                        {template.name}
                                      </span>
                                      {template.dateblock_compute_strategy
                                        ?.name && (
                                        <span className="shrink-0">
                                          <ComputeStrategyTag
                                            label={
                                              COMPUTE_STRATEGY_LABELS[
                                                template
                                                  .dateblock_compute_strategy
                                                  .key
                                              ]?.label
                                            }
                                            hintText={
                                              COMPUTE_STRATEGY_LABELS[
                                                template
                                                  .dateblock_compute_strategy
                                                  .key
                                              ]?.dateblockHint
                                            }
                                            size="small"
                                          />
                                        </span>
                                      )}
                                    </span>
                                  }
                                >
                                  <span className="inline-flex items-center gap-2 w-full">
                                    <span className="truncate">
                                      {template.name}
                                    </span>
                                    {template.dateblock_compute_strategy
                                      ?.name && (
                                      <span className="shrink-0">
                                        <ComputeStrategyTag
                                          label={
                                            COMPUTE_STRATEGY_LABELS[
                                              template
                                                .dateblock_compute_strategy.key
                                            ]?.label
                                          }
                                          hintText={
                                            COMPUTE_STRATEGY_LABELS[
                                              template
                                                .dateblock_compute_strategy.key
                                            ]?.dateblockHint
                                          }
                                          size="small"
                                        />
                                      </span>
                                    )}
                                  </span>
                                </Select.Option>
                              )
                            )}
                          </Select.OptGroup>
                        )}
                    </Select>
                    <Button
                      disabled={!selectedTemplate || downloadingTemplate}
                      loading={downloadingTemplate}
                      onClick={handleDownloadTemplate}
                      data-testid="index-button-download-template"
                    >
                      Download
                    </Button>
                  </div>
                  {selectedTemplate && (
                    <div
                      className="mb-2 pt-[10px]"
                      data-testid="index-download-format-instructions"
                    >
                      <p className="font-semibold mb-1">Instructions:</p>
                      {selectedTemplate === "standard" ? (
                        <ul className="list-disc pl-5 m-0">
                          <li>
                            <span className="opacity-60">
                              Enter {entityLabelSingular} information in the
                              provided format. The first row must not be
                              modified.
                            </span>
                          </li>
                          <li>
                            <span className="opacity-60">
                              {entityLabelPlural} will be created with the
                              default {entityLabelSingular} Template (Standard).
                            </span>
                          </li>
                        </ul>
                      ) : featureType === FeatureTypes.BID_PACKAGE ? (
                        <ul
                          className="list-disc pl-5 m-0"
                          data-testid="index-bid-package-workflow-instructions"
                        >
                          <li>
                            <strong className="font-normal">
                              Enter {entityLabelSingular} information using the
                              provided format.
                            </strong>
                            <span className="opacity-60">
                              {" "}
                              Do not modify the top 12 rows.
                            </span>
                          </li>
                          <li>
                            <strong className="font-normal">
                              {entityLabelSingular} Number and Title are
                              mandatory for every row.
                            </strong>
                            <span className="opacity-60">
                              {" "}
                              Duplicate Titles are not allowed within a single
                              file.
                            </span>
                          </li>
                          <li>
                            <strong className="font-normal">
                              Enter the number of days for each Duration under
                              its corresponding column header.
                            </strong>
                          </li>
                          <li>
                            <strong className="font-normal">
                              Blank cells are ignored during import.
                            </strong>
                            <span className="opacity-60">
                              {" "}
                              Existing values in the system will remain
                              unchanged if a cell is left blank.
                            </span>
                          </li>
                          <li>
                            <strong className="font-normal">
                              Updates follow existing system rules.
                            </strong>
                            <span className="opacity-60">
                              {" "}
                              Some fields may not be updated based on current
                              conditions (for example, Planned Float for{" "}
                              {entityLabelPlural} already in progress).
                            </span>
                          </li>
                        </ul>
                      ) : (
                        <ul className="list-disc pl-5 m-0">
                          <li>
                            <strong className="font-normal">
                              Enter {entityLabelSingular} information using the
                              provided format
                            </strong>
                            <span className="opacity-60">
                              . Do not modify the top 12 rows.
                            </span>
                          </li>
                          <li>
                            <strong className="font-normal">
                              To link a {entityLabelSingular} to Submittals:
                            </strong>
                            <span className="opacity-60">
                              {" "}
                              Enter the Submittal ID as shown in the source
                              system and in the Submittal Log.
                            </span>
                          </li>
                          <li>
                            <strong className="font-normal">
                              To link a {entityLabelSingular} to Activities:
                            </strong>
                            <span className="opacity-60">
                              {" "}
                              Enter the Activity ID from the Active Schedule.
                            </span>
                          </li>
                          <li>
                            <strong className="font-normal">
                              To link multiple Submittals or Activities:
                            </strong>
                            <span className="opacity-60">
                              {" "}
                              Enter multiple IDs in the same cell, separated by
                              a pipe (|) symbol.
                            </span>
                          </li>
                          <li>
                            <strong className="font-normal">
                              Blank cells are ignored during import.
                            </strong>
                            <span className="opacity-60">
                              {" "}
                              Existing values in the system will remain
                              unchanged if a cell is left blank.
                            </span>
                          </li>
                          <li>
                            <strong className="font-normal">
                              Updates follow existing system rules.
                            </strong>
                            <span className="opacity-60">
                              {" "}
                              Some fields may not be updated based on current
                              conditions (for example, Planned Float for{" "}
                              {entityLabelPlural} already in progress).
                            </span>
                          </li>
                        </ul>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <p
              className="mb-1 font-semibold m-0"
              data-testid="index-upload-file-title"
            >
              Upload File
            </p>
            {uploadErrorMessage && (
              <Alert
                className="mb-2"
                style={{ borderRadius: 6 }}
                type="error"
                showIcon
                closable
                // antd v6 renamed alert internals (ant-alert-message -> ant-alert-title), breaking class-based selectors; wrap the message in a span with a stable data-testid for UI automation.
                title={
                  <span data-testid="import-error-alert-message">
                    {uploadErrorMessage}
                  </span>
                }
                onClose={() => setUploadErrorMessage(null)}
                data-testid="index-upload-error-alert"
              />
            )}
            <div className="w-full">
              <Dragger
                {...props}
                disabled={isImportInProgress}
                data-testid="index-dragger-upload"
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                {fileList.length > 0 ? (
                  <div className="flex px-3 justify-center items-center">
                    <Tooltip title={fileList[0].name}>
                      <p
                        className="ant-upload-text truncate max-w-[330px]"
                        data-testid="index-uploaded-filename"
                      >
                        {fileList[0].name}
                      </p>
                    </Tooltip>
                  </div>
                ) : (
                  <div>
                    <p
                      className="ant-upload-text"
                      data-testid="index-upload-instruction"
                    >
                      Click or drag file to upload.
                    </p>
                    <p
                      className="ant-upload-hint"
                      data-testid="index-upload-hint"
                    >
                      Only .csv files are supported.
                    </p>
                  </div>
                )}
              </Dragger>
            </div>
            <div className="flex justify-center">
              {!isImportInProgress && (
                <Button
                  type="primary"
                  disabled={!fileList.length || uploadingState.uploading}
                  onClick={handleUpload}
                  loading={uploadingState.uploading}
                  data-testid="index-button-import"
                >
                  Import
                </Button>
              )}

              {uploadingState.processingStatus === "PROCESSING" && (
                <div
                  className="flex flex-col items-center mt-4 gap-2"
                  data-testid="index-spin-processing"
                >
                  <Spin size="medium" />
                  <p className="font-semibold m-0 text-sm">
                    Import in progress
                  </p>
                  <p className="text-center m-0" style={{ opacity: "60%" }}>
                    Import may take a couple of minutes. Please remain on this
                    page until import is complete
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
